// src/pages/StudentPortal.jsx
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { getStudent, saveStudentProfile } from "../firebase/database";
import StudentDashboard from "./StudentDashboard";
import StudentProgressCheckup from "./StudentProgressCheckup";
import LogTimelineReport from "./LogTimelineReport";
import DietAnalysis from "./DietAnalysis";
import DietLog from "../components/DietLog";
import StudentDiaryView from "../components/StudentDiaryView";
import AIChat from "../components/AIChat";
import AIAssistant from "../components/AIAssistant";
import { updatePassword, changePasswordSecurely } from "../firebase/auth";

import { theme } from "../utils/theme";
import { todayStr } from "../utils/helpers";

const C = theme.colors;

export default function StudentPortal({ view = "overview" }) {
  const { studentId, userData, isLimitedStaff } = useAuth();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (studentId) {
      getStudent(studentId).then(data => {
        setStudent(data);
        setLoading(false);
      });
    }
  }, [studentId]);

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: C.muted, fontFamily: "DM Sans" }}>Loading Portal...</div>;
  if (!studentId) return <div style={{ padding: 40, textAlign: "center", color: C.red, fontFamily: "DM Sans" }}>{isLimitedStaff ? "Staff ID" : "Student ID"} not found. Please contact support.</div>;

  return (
    <div className="fade" style={{ width: "100%", fontFamily: "DM Sans, sans-serif" }}>
      <style>{`
        @keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}} 
        .fade{animation:fadeIn .3s ease}
        .portal-card { background: ${C.card}; border: 1px solid ${C.border}; borderRadius: 12px; padding: 24px; margin-bottom: 24px; }
      `}</style>

      {view === "overview" && (
        <div className="fade">
          <AIAssistant />
          <div style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 24, fontWeight: 700, color: C.text, margin: 0 }}>Welcome, {student?.name || (isLimitedStaff ? "Staff Member" : "Student")}! 👋</h2>
            <p style={{ fontSize: 14, color: C.muted, margin: "4px 0 0 0" }}>Manage your courses, logs, and AI help from here.</p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
             <div className="portal-card" style={{ cursor: "pointer" }} onClick={() => window.location.href="/student/courses"}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>📚</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 8px 0" }}>My Courses</h3>
                <p style={{ fontSize: 14, color: C.muted }}>Access your enrolled courses and lessons.</p>
             </div>
             <div className="portal-card" style={{ cursor: "pointer" }} onClick={() => window.location.href="/student/self-log"}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>📝</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 8px 0" }}>Self Log</h3>
                <p style={{ fontSize: 14, color: C.muted }}>Log your daily work, study, and diet.</p>
             </div>
             <div className="portal-card" style={{ cursor: "pointer" }} onClick={() => window.location.href="/student/log-report"}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>📊</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 8px 0" }}>Log Timeline</h3>
                <p style={{ fontSize: 14, color: C.muted }}>View your progress history over time.</p>
             </div>
             <div className="portal-card" style={{ cursor: "pointer" }} onClick={() => window.location.href="/student/diet-report"}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>🍱</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 8px 0" }}>Diet Timeline</h3>
                <p style={{ fontSize: 14, color: C.muted }}>Analyze your nutritional trends.</p>
             </div>
             <div className="portal-card" style={{ cursor: "pointer" }} onClick={() => window.location.href="/typing-tutor"}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>⌨</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 8px 0" }}>Typing Tutor</h3>
                <p style={{ fontSize: 14, color: C.muted }}>Improve your typing speed in English & Hindi.</p>
             </div>
             <div className="portal-card" style={{ cursor: "pointer" }} onClick={() => window.location.href="/student/ai-assistant"}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>✨</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 8px 0" }}>AI Assistant</h3>
                <p style={{ fontSize: 14, color: C.muted }}>Get instant help from Life AI.</p>
             </div>
             <div className="portal-card" style={{ cursor: "pointer" }} onClick={() => window.location.href="/student/password"}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>🔑</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 8px 0" }}>Change Password</h3>
                <p style={{ fontSize: 14, color: C.muted }}>Update your account security.</p>
             </div>
             <div className="portal-card" style={{ cursor: "pointer" }} onClick={() => window.location.href="/student/profile"}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>👤</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 8px 0" }}>Update Profile</h3>
                <p style={{ fontSize: 14, color: C.muted }}>Manage your personal information.</p>
             </div>
             <div className="portal-card" style={{ cursor: "pointer" }} onClick={() => window.location.href="/student/diary"}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>📔</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 8px 0" }}>My Diary</h3>
                <p style={{ fontSize: 14, color: C.muted }}>View your daily learning progress logs.</p>
             </div>
          </div>
        </div>
      )}

      {view === "courses" && (
        <div className="fade">
          <StudentDashboard studentId={studentId} onBack={() => window.location.href="/student"} />
        </div>
      )}

      {view === "self-log" && (
        <div className="fade">
          <StudentProgressCheckup studentId={studentId} studentName={student?.name} />
        </div>
      )}

      {view === "log-report" && (
        <div className="fade">
          <div style={{ marginBottom: 20 }}>
            <h2 style={{ fontSize: 20, fontWeight: 700 }}>Log Timeline</h2>
            <p style={{ fontSize: 13, color: C.muted }}>Historical view of your work and study logs.</p>
          </div>
          <LogTimelineReport studentId={studentId} />
        </div>
      )}

      {view === "diet-report" && (
        <div className="fade">
          <div style={{ marginBottom: 20 }}>
            <h2 style={{ fontSize: 20, fontWeight: 700 }}>Diet Timeline</h2>
            <p style={{ fontSize: 13, color: C.muted }}>Historical view of your nutritional intake.</p>
          </div>
          <DietAnalysis studentId={studentId} />
        </div>
      )}

      {view === "skills" && (
        <div className="fade">
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20 }}>My Skills</h2>
          <p>Skills component for students will go here.</p>
        </div>
      )}

      {view === "ai-assistant" && (
        <div className="fade" style={{ height: "calc(100vh - 200px)" }}>
           <AIChat isPublic={false} />
        </div>
      )}

      {view === "password" && (
        <div className="fade">
           <ChangePasswordForm />
        </div>
      )}

      {view === "profile" && (
        <div className="fade">
           <ProfileForm student={student} studentId={studentId} isStaff={isLimitedStaff} />
        </div>
      )}

      {view === "diary" && (
        <div className="fade">
           <div style={{ marginBottom: 20 }}>
            <h2 style={{ fontSize: 20, fontWeight: 700 }}>Learning Diary</h2>
            <p style={{ fontSize: 13, color: C.muted }}>Your history of completed topics and lessons.</p>
          </div>
          <StudentDiaryView studentId={studentId} studentName={student?.name} />
        </div>
      )}
    </div>
  );
  }

  function ProfileForm({ student, studentId, isStaff }) {
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ text: "", type: "" });
  const [form, setForm] = useState({ ...student });

  const calculateHealth = (updatedForm) => {
  const w = parseFloat(updatedForm.weight);
  const hFt = parseFloat(updatedForm.heightFeet);
  const hIn = parseFloat(updatedForm.heightInches);

  if (w > 0 && hFt > 0) {
    const totalInches = (hFt * 12) + (hIn || 0);
    const heightM = totalInches * 0.0254;
    const heightCm = heightM * 100;
    const bmi = (w / (heightM * heightM)).toFixed(1);

    let age = 25;
    if (updatedForm.dob) {
      const birthDate = new Date(updatedForm.dob);
      age = new Date().getFullYear() - birthDate.getFullYear();
    }

    let bmr;
    if (updatedForm.gender === "Female") bmr = (10 * w) + (6.25 * heightCm) - (5 * age) - 161;
    else bmr = (10 * w) + (6.25 * heightCm) - (5 * age) + 5;

    const kcal = Math.round(bmr * 1.2);
    return { ...updatedForm, bmi, dailyKcalTarget: kcal };
  }
  return updatedForm;
  };

  const handleHealthChange = (key, val) => {
  setForm(prev => {
    const next = { ...prev, [key]: val };
    return calculateHealth(next);
  });
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setSaving(true);
  setMsg({ text: "", type: "" });
  try {
    await saveStudentProfile(studentId, form);
    setMsg({ text: "Profile updated successfully!", type: "success" });
  } catch (err) {
    setMsg({ text: err.message, type: "error" });
  }
  setSaving(false);
  };

  const inp = (label, key, type = "text", options = null) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ fontSize: 12, color: "#6B7280", display: "block", marginBottom: 6, fontWeight: 600 }}>{label}</label>
    {options ? (
      <select value={form[key] || ""} onChange={e => setForm({ ...form, [key]: e.target.value })} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #E5E7EB", outline: "none", background: "#fff", fontSize: 14 }}>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    ) : (
      <input type={type} value={form[key] || ""} onChange={e => setForm({ ...form, [key]: e.target.value })} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #E5E7EB", outline: "none", fontSize: 14 }} />
    )}
  </div>
  );

  return (
  <div style={{ maxWidth: 800, margin: "0 auto", padding: "20px 0" }}>
    <div className="portal-card">
      <h3 style={{ marginBottom: 24, fontSize: 20, fontWeight: 700 }}>My Profile Information</h3>

      {msg.text && (
        <div style={{ padding: "12px 16px", borderRadius: 10, fontSize: 13, marginBottom: 24, border: "1px solid", 
          background: msg.type === "success" ? "#ECFDF5" : "#FEF2F2", 
          borderColor: msg.type === "success" ? "#10B98144" : "#EF444444",
          color: msg.type === "success" ? "#059669" : "#DC2626" 
        }}>
          {msg.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#3D7EFF", marginBottom: 16, borderBottom: "1px solid #E5E7EB", paddingBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
          Basic Details
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
          {inp("Full Name", "name")}
          {inp("Email Address", "email", "email")}
          {inp("Mobile Number", "phone")}
          {inp("Family Mobile Number", "familyPhone")}
          {inp("Date of Birth", "dob", "date")}
          {inp("Gender", "gender", "select", ["Male", "Female", "Other"])}
        </div>

        <div style={{ fontSize: 13, fontWeight: 700, color: "#3D7EFF", marginTop: 24, marginBottom: 16, borderBottom: "1px solid #E5E7EB", paddingBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
          Health & Fitness
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 20px" }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: "#6B7280", display: "block", marginBottom: 6, fontWeight: 600 }}>Weight (kg)</label>
            <input type="number" step="0.1" value={form.weight || ""} onChange={e => handleHealthChange("weight", e.target.value)} style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #E5E7EB", outline: "none", fontSize: 14 }} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: "#6B7280", display: "block", marginBottom: 6, fontWeight: 600 }}>Height (Feet)</label>
            <input type="number" value={form.heightFeet || ""} onChange={e => handleHealthChange("heightFeet", e.target.value)} style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #E5E7EB", outline: "none", fontSize: 14 }} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: "#6B7280", display: "block", marginBottom: 6, fontWeight: 600 }}>Height (Inches)</label>
            <input type="number" value={form.heightInches || ""} onChange={e => handleHealthChange("heightInches", e.target.value)} style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #E5E7EB", outline: "none", fontSize: 14 }} />
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px", marginBottom: 16 }}>
           <div style={{ background: "#F3F4F6", padding: "12px 16px", borderRadius: 10 }}>
              <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 4 }}>BODY MASS INDEX (BMI)</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#3D7EFF" }}>{form.bmi || "--"}</div>
           </div>
           <div style={{ background: "#F3F4F6", padding: "12px 16px", borderRadius: 10 }}>
              <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 4 }}>EST. DAILY KCAL TARGET</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#16A349" }}>{form.dailyKcalTarget ? `${form.dailyKcalTarget} kcal` : "--"}</div>
           </div>
        </div>

        <div style={{ fontSize: 13, fontWeight: 700, color: "#3D7EFF", marginTop: 24, marginBottom: 16, borderBottom: "1px solid #E5E7EB", paddingBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
          {isStaff ? "Professional Details" : "Academic Details"}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
          {isStaff ? inp("Designation", "designation") : inp("Course Joined", "courseJoined")}
          {isStaff ? inp("Employment Type", "employmentType") : inp("Referred By", "referredBy")}
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ fontSize: 12, color: "#6B7280", display: "block", marginBottom: 6, fontWeight: 600 }}>Permanent Address</label>
          <textarea value={form.address || ""} onChange={e => setForm({ ...form, address: e.target.value })} style={{ width: "100%", padding: 12, borderRadius: 8, border: "1px solid #E5E7EB", outline: "none", minHeight: 80, fontSize: 14, fontFamily: "inherit" }} />
        </div>

        <button type="submit" disabled={saving} style={{ width: "100%", padding: "16px", background: "#3D7EFF", color: "#fff", border: "none", borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", boxShadow: "0 4px 12px rgba(61, 126, 255, 0.3)", transition: ".2s" }}>
          {saving ? "Saving Changes..." : "Update My Profile →"}
        </button>
      </form>
    </div>
  </div>
  );
  }
function ChangePasswordForm() {
  const [currentPass, setCurrentPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ text: "", type: "" });

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!currentPass) return setMsg({ text: "Please enter your current password.", type: "error" });
    if (newPass.length < 6) return setMsg({ text: "New password must be at least 6 characters.", type: "error" });
    if (newPass !== confirmPass) return setMsg({ text: "Passwords do not match.", type: "error" });
    
    setLoading(true);
    setMsg({ text: "", type: "" });
    try {
      await changePasswordSecurely(currentPass, newPass);
      setMsg({ text: "Password updated successfully!", type: "success" });
      setCurrentPass(""); setNewPass(""); setConfirmPass("");
    } catch (err) {
      let errorMsg = err.message;
      if (err.code === "auth/wrong-password") errorMsg = "Incorrect current password.";
      setMsg({ text: errorMsg, type: "error" });
    }
    setLoading(false);
  };

  return (
    <div style={{ maxWidth: 400, margin: "0 auto", padding: "20px 0" }}>
      <div className="portal-card">
        <h3 style={{ marginBottom: 20, fontSize: 18, fontWeight: 700 }}>Update Account Password</h3>
        
        {msg.text && (
          <div style={{ padding: "12px 16px", borderRadius: 10, fontSize: 13, marginBottom: 20, border: "1px solid", 
            background: msg.type === "success" ? "#ECFDF5" : "#FEF2F2", 
            borderColor: msg.type === "success" ? "#10B98144" : "#EF444444",
            color: msg.type === "success" ? "#059669" : "#DC2626" 
          }}>
            {msg.text}
          </div>
        )}

        <form onSubmit={handleUpdate}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: "#6B7280", display: "block", marginBottom: 6, fontWeight: 600 }}>Current Password</label>
            <input type="password" value={currentPass} onChange={e => setCurrentPass(e.target.value)} required placeholder="Enter current password"
              style={{ width: "100%", background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 10, padding: "12px 14px", fontSize: 14, outline: "none" }} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: "#6B7280", display: "block", marginBottom: 6, fontWeight: 600 }}>New Password</label>
            <input type="password" value={newPass} onChange={e => setNewPass(e.target.value)} required minLength={6} placeholder="Enter new password"
              style={{ width: "100%", background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 10, padding: "12px 14px", fontSize: 14, outline: "none" }} />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ fontSize: 12, color: "#6B7280", display: "block", marginBottom: 6, fontWeight: 600 }}>Confirm New Password</label>
            <input type="password" value={confirmPass} onChange={e => setConfirmPass(e.target.value)} required minLength={6} placeholder="Repeat new password"
              style={{ width: "100%", background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 10, padding: "12px 14px", fontSize: 14, outline: "none" }} />
          </div>
          <button type="submit" disabled={loading} style={{ width: "100%", padding: "14px", background: "#3D7EFF", color: "#fff", border: "none", borderRadius: 10, cursor: loading ? "not-allowed" : "pointer", fontSize: 15, fontWeight: 700, transition: ".2s" }}>
            {loading ? "Verifying & Updating..." : "Confirm Password Change"}
          </button>
        </form>
      </div>
    </div>
  );
}
